import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AssetType,
  EAssetTypeVariant,
  Flex,
  Icon,
  Skeleton,
  Text,
  cn,
} from '@kognitos/lattice';
import {
  useListAssets,
  useListFolders,
} from '@/generated/hooks/bishop/collections/v1/api';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';
import { getAssetTypeFromMimeType, toAssetView, toFolderView } from '../utils';
import type { IAssetView, IFolderView } from '../types';

// Page size per tree level — keep it modest; pagination is a follow-up.
const PAGE_SIZE = 50;

interface ITreeRowProps {
  name: string;
  assetType: ReturnType<typeof getAssetTypeFromMimeType> | EAssetTypeVariant;
  /** Nesting level — drives the leading indent while keeping the row full width. */
  depth: number;
  /** Folder rows render a chevron; file rows render an aligning spacer. */
  expandable?: boolean;
  expanded?: boolean;
  selected?: boolean;
  onClick: () => void;
}

function TreeRow({
  name,
  assetType,
  depth,
  expandable = false,
  expanded = false,
  selected = false,
  onClick,
}: ITreeRowProps) {
  return (
    <Flex
      align="center"
      gap={2}
      width="full"
      className={cn(
        'cursor-pointer rounded-md border border-transparent px-2 py-1.5 transition-colors hover:bg-accent/40',
        selected && 'border-primary bg-accent/60',
      )}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
    >
      {/* Indent via leading spacers so the row (and its hover) stays full width.
          The spacers are identical and positional, so an index key is correct. */}
      {Array.from({ length: depth }).map((_, index) => (
        // eslint-disable-next-line react-x/no-array-index-key -- static positional spacer
        <span key={index} className="w-4 shrink-0" />
      ))}
      {expandable ? (
        <Icon
          type={expanded ? 'ChevronDown' : 'ChevronRight'}
          size="sm"
          className="shrink-0 text-muted-foreground"
        />
      ) : (
        <span className="size-4 shrink-0" />
      )}
      <AssetType type={assetType} className="shrink-0" />
      <Text level="small" className="flex-1 truncate">
        {name}
      </Text>
    </Flex>
  );
}

interface ILevelProps {
  /** Bishop folder resource name; "" at the workspace root. */
  parentFolderName: string;
  /** Nesting level of the rows this level renders. */
  depth: number;
  selectedAssetNames: Set<string>;
  onAssetSelect: (asset: IAssetView) => void;
}

/**
 * One level of the tree. Fetches the folders and assets directly under
 * `parentFolderName`; child levels mount (and fetch) only when their folder is
 * expanded, so the tree loads lazily.
 */
function AssetTreeLevel({
  parentFolderName,
  depth,
  selectedAssetNames,
  onAssetSelect,
}: ILevelProps) {
  const { t } = useTranslation('library');
  const orgId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  const { data: foldersData, isLoading: isLoadingFolders } = useListFolders(
    orgId,
    workspaceId,
    { page_size: PAGE_SIZE, parent_folder_name: parentFolderName },
  );
  const { data: assetsData, isLoading: isLoadingAssets } = useListAssets(
    orgId,
    workspaceId,
    { page_size: PAGE_SIZE, folder_name: parentFolderName },
  );

  const folders = (foldersData?.data?.folders ?? []).map(toFolderView);
  const assets = (assetsData?.data?.assets ?? []).map(toAssetView);
  const isLoading = isLoadingFolders || isLoadingAssets;

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (folders.length === 0 && assets.length === 0) {
    return (
      <Text level="small" color="muted" className="px-2 py-1.5">
        {t('library.dialog.moveAsset.emptyFolder')}
      </Text>
    );
  }

  return (
    <>
      {folders.map(folder => (
        <AssetTreeFolder
          key={folder.name}
          folder={folder}
          depth={depth}
          selectedAssetNames={selectedAssetNames}
          onAssetSelect={onAssetSelect}
        />
      ))}
      {assets.map(asset => (
        <TreeRow
          key={asset.name}
          name={asset.display_name}
          assetType={getAssetTypeFromMimeType(asset.mime_type)}
          depth={depth}
          selected={selectedAssetNames.has(asset.name ?? '')}
          onClick={() => onAssetSelect(asset)}
        />
      ))}
    </>
  );
}

interface IFolderProps {
  folder: IFolderView;
  depth: number;
  selectedAssetNames: Set<string>;
  onAssetSelect: (asset: IAssetView) => void;
}

function AssetTreeFolder({
  folder,
  depth,
  selectedAssetNames,
  onAssetSelect,
}: IFolderProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TreeRow
        name={folder.display_name}
        assetType={EAssetTypeVariant.Folder}
        depth={depth}
        expandable
        expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
      />
      {expanded && (
        <AssetTreeLevel
          parentFolderName={folder.name ?? ''}
          depth={depth + 1}
          selectedAssetNames={selectedAssetNames}
          onAssetSelect={onAssetSelect}
        />
      )}
    </>
  );
}

interface IAssetManagerTreeProps {
  selectedAssetNames: Set<string>;
  onAssetSelect: (asset: IAssetView) => void;
}

/**
 * Expandable folder/asset tree used by the file-select dialog. Folders expand
 * inline to reveal their contents; clicking a file toggles its selection.
 */
export function AssetManagerTree({
  selectedAssetNames,
  onAssetSelect,
}: IAssetManagerTreeProps) {
  return (
    <Flex
      direction="column"
      gap={0.5}
      width="full"
      className="h-[320px] min-w-0 overflow-y-auto"
    >
      <AssetTreeLevel
        parentFolderName=""
        depth={0}
        selectedAssetNames={selectedAssetNames}
        onAssetSelect={onAssetSelect}
      />
    </Flex>
  );
}

export default AssetManagerTree;
